import * as React from 'react';
import { cn } from '@/lib/utils';
import { CDCheckbox } from '../primitives/CDCheckbox';
import { CDSectionHeader } from '../primitives/CDSectionHeader';

export interface OtherDisclosuresData {
  appraisal: {
    rightToCopy: boolean;
    appraisalDetails?: string;
  };
  contract: {
    details?: string;
  };
  liabilityAfterForeclosure: {
    hasDeficiencyRisk: boolean;
    stateAllowsDeficiency: boolean;
    details?: string;
  };
  refinance: {
    refinancingSameProperty: boolean;
    details?: string;
  };
  taxDeductions: {
    details?: string;
  };
}

export interface CDOtherDisclosuresProps {
  /** Other disclosures data */
  data: Partial<OtherDisclosuresData>;
  /** Whether fields are editable */
  editable?: boolean;
  /** onChange handler */
  onChange?: (data: Partial<OtherDisclosuresData>) => void;
  /** Additional className */
  className?: string;
}

/**
 * CDOtherDisclosures - Other Disclosures section on Page 5
 *
 * Contains: Appraisal, Contract, Liability after Foreclosure, Refinance, Tax Deductions
 */
export const CDOtherDisclosures = React.forwardRef<HTMLDivElement, CDOtherDisclosuresProps>(
  ({ data, editable = false, onChange, className }, ref) => {
    // Helper to update liability after foreclosure
    const updateLiabilityAfterForeclosure = (stateAllowsDeficiency: boolean) => {
      const currentLiability = data.liabilityAfterForeclosure ?? {
        hasDeficiencyRisk: false,
        stateAllowsDeficiency: false,
      };
      onChange?.({
        ...data,
        liabilityAfterForeclosure: {
          ...currentLiability,
          stateAllowsDeficiency,
        },
      });
    };

    return (
      <div ref={ref} className={cn('', className)}>
        <CDSectionHeader title="Other Disclosures" />

        <div className="border border-t-0 border-cd-black">
          <div className="grid grid-cols-2 gap-0">
            {/* Left Column */}
            <div className="border-r border-cd-black">
              {/* Appraisal */}
              <div className="border-b border-cd-black p-1.5">
                <div className="cd-text-small font-bold mb-0.5">Appraisal</div>
                <div className="cd-text-tiny">
                  If the property was appraised for your loan, your lender is required to give you a
                  copy at no additional cost at least 3 days before closing. If you have not yet
                  received it, please contact your lender at the information listed below.
                </div>
              </div>

              {/* Contract */}
              <div className="border-b border-cd-black p-1.5">
                <div className="cd-text-small font-bold mb-0.5">Contract</div>
                <div className="cd-text-tiny">
                  There are additional terms and conditions in your loan documents, including your
                  promissory note and any security instruments. You should review these documents
                  for the complete terms of your obligation.
                </div>
              </div>

              {/* Liability after Foreclosure */}
              <div className="p-1.5">
                <div className="cd-text-small font-bold mb-0.5">Liability after Foreclosure</div>
                {editable ? (
                  <div className="cd-text-tiny">
                    <div className="flex items-start gap-1 mb-1">
                      <CDCheckbox
                        checked={data.liabilityAfterForeclosure?.stateAllowsDeficiency === true}
                        size="sm"
                        onChange={() => updateLiabilityAfterForeclosure(true)}
                      />
                      <span>State law may protect you from liability</span>
                    </div>
                    <div className="flex items-start gap-1">
                      <CDCheckbox
                        checked={data.liabilityAfterForeclosure?.stateAllowsDeficiency === false}
                        size="sm"
                        onChange={() => updateLiabilityAfterForeclosure(false)}
                      />
                      <span>State law does not protect you from deficiency judgments</span>
                    </div>
                  </div>
                ) : (
                  <div className="cd-text-tiny">
                    {data.liabilityAfterForeclosure?.stateAllowsDeficiency ? (
                      <>
                        If your lender forecloses on this property and the foreclosure does not
                        cover the amount of unpaid balance on this loan, state law may protect you
                        from liability for the unpaid balance. If you refinance or take on any
                        additional debt on this property, you may lose this protection and have to
                        pay any debt remaining even after foreclosure. You may want to consult a
                        lawyer for more information.
                      </>
                    ) : (
                      <>
                        State law does not protect you from deficiency judgments. If your lender
                        forecloses on this property and the foreclosure does not cover the amount of
                        unpaid balance on this loan, you may still owe your lender the remaining
                        balance.
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Right Column */}
            <div>
              {/* Refinance */}
              <div className="border-b border-cd-black p-1.5">
                <div className="cd-text-small font-bold mb-0.5">Refinance</div>
                <div className="cd-text-tiny">
                  Refinancing this loan will depend on your future financial situation, the property
                  value, and market conditions. You may not be able to refinance this loan.
                </div>
              </div>

              {/* Tax Deductions */}
              <div className="p-1.5">
                <div className="cd-text-small font-bold mb-0.5">Tax Deductions</div>
                <div className="cd-text-tiny">
                  If you borrow more than this property is worth, the interest on the loan amount
                  above this property's fair market value is not deductible from your federal income
                  taxes. You should consult a tax advisor for more information.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  },
);

CDOtherDisclosures.displayName = 'CDOtherDisclosures';
